import {
  HOME_COUNTRY_CODE,
  ORG_CITY_MAX,
  ORG_DEFAULT_MAX_MEMBERS,
  ORG_LEGAL_NAME_MAX,
  ORG_MAX_MEMBERS_CAP,
  ORG_NAME_MAX,
  ORG_PHONE_PATTERN,
  ORG_SITE_MAX,
  ORG_TELEGRAM_PATTERN,
  ORG_UNP_PATTERN,
  type AdminOrgDto,
  type OrgContactsDto,
} from '@carcheck/shared';
import { z } from 'zod';

import type { CreateOrgBody, UpdateOrgBody } from '../../../api/admin-orgs';

/**
 * The company form (AUT-403) — create page and edit dialog share it. Every
 * field is a string in the form; the limits and patterns are the API's own
 * (`@carcheck/shared`), so a value the form accepts is one the server takes.
 *
 * Forgiving input, strict wire: the phone may carry spaces / dashes /
 * brackets (stripped), Telegram may start with `@` (stripped — the brand
 * renders it), a site without a scheme gets `https://`. Empty optional
 * fields are omitted on create and sent as `null` on edit (clears them).
 * No «daily ceiling» field — the owner dropped caps (plan decision 10).
 */
const E = 'companies.form.errors';

export const normalizePhone = (v: string): string => v.replace(/[\s\-()]/g, '');
export const normalizeTelegram = (v: string): string => v.trim().replace(/^@/, '');
export const normalizeSite = (v: string): string => {
  const trimmed = v.trim();
  if (trimmed === '') return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isUrl = (v: string): boolean => {
  try {
    return new URL(v).hostname.includes('.');
  } catch {
    return false;
  }
};

const blankOr = (test: (v: string) => boolean) => (v: string) => v.trim() === '' || test(v);

export const companyFormSchema = z.object({
  name: z.string().trim().min(1, `${E}.required`).max(ORG_NAME_MAX, `${E}.tooLong`),
  // A dictionary key, or the company's own text when `cityCustom` (AUT-761).
  city: z.string().trim().max(ORG_CITY_MAX, `${E}.tooLong`),
  /** ISO code; `''` = not chosen (Belarus, the server's rule). */
  cityCountry: z.string(),
  cityCustom: z.boolean(),
  legalName: z.string().trim().max(ORG_LEGAL_NAME_MAX, `${E}.tooLong`),
  unp: z.string().refine(
    blankOr((v) => ORG_UNP_PATTERN.test(v.trim())),
    `${E}.unp`,
  ),
  phone: z.string().refine(
    blankOr((v) => ORG_PHONE_PATTERN.test(normalizePhone(v))),
    `${E}.phone`,
  ),
  telegram: z.string().refine(
    blankOr((v) => ORG_TELEGRAM_PATTERN.test(normalizeTelegram(v))),
    `${E}.telegram`,
  ),
  site: z.string().refine(
    blankOr((v) => {
      const url = normalizeSite(v);
      return url.length <= ORG_SITE_MAX && isUrl(url);
    }),
    `${E}.site`,
  ),
  maxMembers: z.string().refine((v) => {
    const n = Number(v.trim());
    return /^\d+$/.test(v.trim()) && n >= 1 && n <= ORG_MAX_MEMBERS_CAP;
  }, `${E}.maxMembers`),
});

export type CompanyFormValues = z.infer<typeof companyFormSchema>;

export const EMPTY_COMPANY_FORM: CompanyFormValues = {
  name: '',
  city: '',
  cityCountry: '',
  cityCustom: false,
  legalName: '',
  unp: '',
  phone: '',
  telegram: '',
  site: '',
  maxMembers: String(ORG_DEFAULT_MAX_MEMBERS),
};

/** The wire value of each contact (`''` = none). */
const contactsOf = (v: CompanyFormValues): Record<keyof OrgContactsDto, string> => ({
  phone: normalizePhone(v.phone),
  telegram: normalizeTelegram(v.telegram),
  site: normalizeSite(v.site),
});

/** The place triple the form holds; `''` country on a city = Belarus. */
interface FormPlace {
  city: string;
  cityCountry: string;
  cityCustom: boolean;
}

const placeFromForm = (v: CompanyFormValues): FormPlace => ({
  city: v.city.trim(),
  cityCountry: v.city.trim() === '' ? '' : v.cityCountry || HOME_COUNTRY_CODE,
  cityCustom: v.city.trim() === '' ? false : v.cityCustom,
});

const placeFromOrg = (org: AdminOrgDto): FormPlace => {
  const city = org.cityCustom ? (org.city ?? '') : (org.cityKey ?? org.city ?? '');
  return {
    city,
    cityCountry: city === '' ? '' : (org.cityCountry ?? HOME_COUNTRY_CODE),
    cityCustom: city === '' ? false : (org.cityCustom ?? false),
  };
};

/** Create body place fields: only what the admin set; a bare city stays as it was. */
const placeOf = (v: CompanyFormValues): Partial<CreateOrgBody> => {
  if (v.city.trim() === '') return {};
  return {
    city: v.city.trim(),
    ...(v.cityCountry ? { cityCountry: v.cityCountry } : {}),
    ...(v.cityCustom ? { cityCustom: true } : {}),
  };
};

export const toCreateBody = (v: CompanyFormValues): CreateOrgBody => {
  const contacts = Object.fromEntries(
    Object.entries(contactsOf(v)).filter(([, value]) => value !== ''),
  );
  return {
    name: v.name.trim(),
    ...(v.legalName.trim() ? { legalName: v.legalName.trim() } : {}),
    ...(v.unp.trim() ? { unp: v.unp.trim() } : {}),
    ...placeOf(v),
    contacts,
    maxMembers: Number(v.maxMembers.trim()),
  };
};

export const valuesFromOrg = (org: AdminOrgDto): CompanyFormValues => ({
  name: org.name,
  ...placeFromOrg(org),
  legalName: org.legalName ?? '',
  unp: org.unp ?? '',
  phone: org.contacts.phone ?? '',
  telegram: normalizeTelegram(org.contacts.telegram ?? ''),
  site: org.contacts.site ?? '',
  maxMembers: String(org.maxMembers),
});

const nullable = (v: string): string | null => (v === '' ? null : v);

/** Only what changed against `org`; `{}` → nothing to save. */
export const toUpdateBody = (v: CompanyFormValues, org: AdminOrgDto): UpdateOrgBody => {
  const body: UpdateOrgBody = {};
  if (v.name.trim() !== org.name) body.name = v.name.trim();
  const texts = { legalName: v.legalName, unp: v.unp } as const;
  for (const key of ['legalName', 'unp'] as const) {
    const next = nullable(texts[key].trim());
    if (next !== org[key]) body[key] = next;
  }
  const was = placeFromOrg(org);
  const now = placeFromForm(v);
  if (
    now.city !== was.city ||
    now.cityCountry !== was.cityCountry ||
    now.cityCustom !== was.cityCustom
  ) {
    body.city = now.city === '' ? null : now.city;
    if (now.city !== '') {
      body.cityCountry = now.cityCountry;
      body.cityCustom = now.cityCustom;
    }
  }
  const maxMembers = Number(v.maxMembers.trim());
  if (maxMembers !== org.maxMembers) body.maxMembers = maxMembers;

  const contacts: NonNullable<UpdateOrgBody['contacts']> = {};
  const next = contactsOf(v);
  for (const key of ['phone', 'telegram', 'site'] as const) {
    const was =
      key === 'telegram' ? normalizeTelegram(org.contacts.telegram ?? '') : org.contacts[key];
    if (nullable(next[key]) !== nullable(was ?? '')) contacts[key] = nullable(next[key]);
  }
  if (Object.keys(contacts).length > 0) body.contacts = contacts;
  return body;
};
